/* eslint-disable class-methods-use-this */
// Intercom chat — migrated out of Google Tag Manager so it loads reliably in
// privacy-hardened browsers (Safari Private Browsing, content blockers) where
// GTM is often blocked.
//
// This file reproduces exactly what the GTM container used to do:
//   - Install tag "WWW Intercom Chat (Live Only)" (app_id ljz51fs1) fired on
//     first-interaction on the homepage / translated homepages / thank-you /
//     a long allow-list of chat pages, and on route changes to the trial /
//     contact pages — restricted to the live www.splashtop.(com|cn) hosts.
//   - Custom HTML tag "Intercom - Show" called Intercom('show') on a route
//     change whose URL fragment is `#open-chat`, with the install tag as its
//     setup tag (so navigating to a `#open-chat` link opens the messenger,
//     loading Intercom first if needed).
// The page/host matchers below are ported verbatim from that container.
import { isBrowser, debug } from '../misc';
import {
  ThirdPartySnippet,
  TRIGGERS,
  isLiveHost,
  isForced,
  isLiveOrForced
} from './snippet';

export const INTERCOM_APP_ID = 'ljz51fs1';
export const INTERCOM_WIDGET_SRC = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;

// URL fragment (without the leading '#') that opens the messenger on navigation.
export const OPEN_CHAT_FRAGMENT = 'open-chat';

// Translated homepage roots, e.g. /cn, /tw, /ja. (GTM rule: path `/(cn|tw|...)$`.)
export const TRANSLATED_HOMEPAGE_RE = /\/(cn|tw|nl|de|it|fr|pt|es|ja)$/i;

// Thank-you pages. (GTM rule: path `/thank-you$`.)
export const THANK_YOU_RE = /\/thank-you$/i;

// Route-change pages that load Intercom immediately on navigation (no
// interaction required). (GTM "WWW Open Chat Pages (Route Change)".)
export const ROUTE_CHANGE_PATHS_RE = /\/(trial|contact-us|lp\/restart-trial|products\/remote-access\/trial-hub)/i;

// The chat-pages allow-list, seeded from GTM "WWW Chat Pages (First Interaction)".
// Stored as a string and compiled below. Matching is case-insensitive and
// unanchored, exactly as GTM's `_re` operator behaved. New chat pages are added
// here — the code, not GTM, is the source of truth going forward. A bare token
// also matches its `/reference/<token>` path because matching is unanchored.
const CHAT_PAGES_PATTERN = '/(reference/autonomous-endpoint-management|autonomous-endpoint-management|agentic-packr|foxpass|lp/restart-trial|lp/remote-access|lp/remote-desktop|lp/remote-pc|lp/remote-connect|lp/remote-computer|lp/remote-login|lp/remote-viewing|lp/splashtop-brand|contact-us|pricing|downloads|business|remote-support|sos|premium|compare/logmein-alternative|logmein-alternative|compare/logmeinpro-alternative|splashtop-personal-vs-splashtop-business-comparison|compare/gotomypc-alternative|remote-desktop-access|accounting|remote-desktop-access-software|compare-business-with-remote-support|vnc-alternative|teamviewer-commercial-use|compare/teamviewer-alternative|teamviewer-alternative|compare/logmein-central-alternative|best-remote-pc-access|classroom|enterprise|remote-support/monitoring-and-management|work-from-home|channelproforum|education|compare/connectwise-control-alternative|chat|distance-remote-learning|remote-labs|on-prem|features/scheduled-remote-access|free-trial|rdp-vpn-alternative|personal|wiredxdisplay|mspconnectliveca|msptechday-may2021|mspconnectliverm|channelpro-april2021|compare/logmein-rescue-alternative|media-and-entertainment|smbtechfest-april2021|iste-june2021|smw-july2021|adobemax-2021|trial/|spiceworld-2021|jnuc-2021|refresh-2021|swl-2021|cp-dec2021|addigyinnovate-2022|anydesk-alternative|msptechday-mar2022|cp-apr2022|augmented-reality|compare/teamviewer-commercial-use-detected|teamviewer-tensor-vs-splashtop|add-ons/endpoint-security-bitdefender|teamviewer-quicksupport-alternative|teamviewer-savings-program|resources|about|events|support|home|remote-access|srs-premium)';
export const CHAT_PAGES_RE = new RegExp(CHAT_PAGES_PATTERN, 'i');

// Normalizes a location hash ('#open-chat' / 'open-chat') to the bare fragment.
export const normalizeHash = (hash) => (hash || '').replace(/^#/, '');

// Whether a first interaction on this host+path should load Intercom. Mirrors
// GTM install rules 7–10 (plus their live-host exceptions). The English
// homepage ('/') is limited to the .com host in GTM; the force flag relaxes
// that so QA can exercise it on previews/localhost.
export const matchesFirstInteraction = (hostname, pathname) => {
  if (!isLiveOrForced(hostname)) return false;
  const path = pathname || '';
  if (path === '/' && (isForced() || /^www\.splashtop\.com$/i.test(hostname))) return true;
  if (TRANSLATED_HOMEPAGE_RE.test(path)) return true;
  if (THANK_YOU_RE.test(path)) return true;
  if (CHAT_PAGES_RE.test(path)) return true;
  return false;
};

// Whether a route change to this host+path(+hash) should load Intercom. Mirrors
// GTM install rule 11, plus the `#open-chat` deep link whose setup tag installed
// Intercom before showing the messenger.
export const matchesRouteChange = (hostname, pathname, hash) => {
  if (!isLiveOrForced(hostname)) return false;
  if (ROUTE_CHANGE_PATHS_RE.test(pathname || '')) return true;
  if (normalizeHash(hash) === OPEN_CHAT_FRAGMENT) return true;
  return false;
};

// Standard Intercom loader (the official snippet, parameterized by app id). If
// Intercom already exists it reattaches/updates instead of re-injecting.
export const bootIntercom = (appId = INTERCOM_APP_ID) => {
  if (!isBrowser) return;
  window.intercomSettings = { ...(window.intercomSettings || {}), app_id: appId };

  const w = window;
  const ic = w.Intercom;
  if (typeof ic === 'function') {
    ic('reattach_activator');
    ic('update', w.intercomSettings);
    return;
  }

  const d = document;
  const i = function intercomQueue(...args) {
    i.c(args);
  };
  i.q = [];
  i.c = function pushToQueue(args) {
    i.q.push(args);
  };
  w.Intercom = i;

  const loadWidget = () => {
    const script = d.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = INTERCOM_WIDGET_SRC;
    const first = d.getElementsByTagName('script')[0];
    if (first && first.parentNode) {
      first.parentNode.insertBefore(script, first);
    } else {
      (d.head || d.documentElement).appendChild(script);
    }
  };

  if (d.readyState === 'complete') {
    loadWidget();
  } else {
    w.addEventListener('load', loadWidget, false);
  }
};

// Keeps Intercom's "Product" attribute in sync with the URL so Foxpass pages
// report `foxpass` and everything else `splashtop`. Ported from gatsby-browser.
export const getIntercomProduct = (pathname = '') => (
  pathname.includes('/foxpass') ? 'foxpass' : 'splashtop'
);

// Opens the messenger. Intercom may still be booting (the loader installs a
// queue stub synchronously, so calls are queued), so retry briefly — this is the
// behavior of the old "Intercom - Show" GTM tag.
const SHOW_RETRY_MS = 300;
const SHOW_RETRY_TIMEOUT_MS = 10000;

export class IntercomSnippet extends ThirdPartySnippet {
  constructor() {
    super({
      id: 'intercom',
      // Functional chat widget: loads on page/interaction triggers rather than
      // behind analytics consent, matching the previous GTM behavior.
      consentCategory: null,
      triggers: [TRIGGERS.FIRST_INTERACTION, TRIGGERS.ROUTE_CHANGE]
    });
    this.lastProduct = null;
    this.onShowAttached = false;
    this.showTimer = null;
  }

  shouldLoad(ctx) {
    if (ctx.trigger === TRIGGERS.FIRST_INTERACTION) {
      return matchesFirstInteraction(ctx.hostname, ctx.pathname);
    }
    if (ctx.trigger === TRIGGERS.ROUTE_CHANGE) {
      return matchesRouteChange(ctx.hostname, ctx.pathname, ctx.hash);
    }
    return false;
  }

  load() {
    bootIntercom(INTERCOM_APP_ID);
    this.syncProduct();
    // Opening the messenger (#open-chat) is handled in onRouteChange, which the
    // manager always runs right after load() on a route change — matching the
    // GTM "Intercom - Show" tag, which only fired on route changes.
  }

  onRouteChange(ctx) {
    this.syncProduct();
    if (normalizeHash(ctx.hash) === OPEN_CHAT_FRAGMENT) this.show();
  }

  syncProduct() {
    if (!isBrowser || typeof window.Intercom !== 'function') return;
    // Re-sync whenever the visitor opens the messenger (URL may have changed).
    if (!this.onShowAttached) {
      this.onShowAttached = true;
      window.Intercom('onShow', () => this.syncProduct());
    }
    const product = getIntercomProduct(window.location.pathname);
    if (this.lastProduct === product) return;
    this.lastProduct = product;
    window.Intercom('update', { Product: product });
    debug('[Intercom] Product', product);
  }

  show() {
    if (!isBrowser) return;
    const tryShow = () => {
      if (typeof window.Intercom === 'function') {
        window.Intercom('show');
        if (this.showTimer) {
          window.clearInterval(this.showTimer);
          this.showTimer = null;
        }
        return true;
      }
      return false;
    };

    if (tryShow() || this.showTimer) return;

    const startedAt = Date.now();
    this.showTimer = window.setInterval(() => {
      if (tryShow()) return;
      if (Date.now() - startedAt >= SHOW_RETRY_TIMEOUT_MS) {
        window.clearInterval(this.showTimer);
        this.showTimer = null;
      }
    }, SHOW_RETRY_MS);
  }
}

const intercomSnippet = new IntercomSnippet();

export default intercomSnippet;

// Re-exported for callers/tests that only need host gating.
export { isLiveHost, isForced };
